import { useQuery } from 'react-query';
import axios from 'axios';
import { TerminalAppearanceAPI } from '../../constants/API';
import {
  TerminalAppearanceResponse,
  TerminalLogoSettings,
  TerminalTabloSettings,
  TerminalTabloSettingsFromBackend
} from '../../types/Admin/TerminalAppearanceTypes';
import { commonHeaders, getMonitorToken, queueScreenHeaders } from '../../constants/HeadersAPI';
import { baseSoundURL } from '../../config';

const defaultLogoFitMode = 'scale';
const defaultLogoScale = 100;
const defaultLogoPosition = 50;

const normalizeMediaUrl = (url: string) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url) || url.startsWith('//')) {
    return url;
  }
  const separator = url.startsWith('/') ? '' : '/';
  return `${baseSoundURL}${separator}${url}`;
};

/** Нормализует настройки логотипа: явные числа для scale/positionX/positionY (API может вернуть строки или null). */
const normalizeTabloLogo = (
  logo: TerminalLogoSettings | null | undefined
): TerminalLogoSettings | null | undefined => {
  if (logo == null) return logo;
  return {
    url: logo.url ? normalizeMediaUrl(logo.url) : logo.url,
    fitMode: logo.fitMode ?? defaultLogoFitMode,
    scale: Number.isFinite(Number(logo.scale)) ? Number(logo.scale) : defaultLogoScale,
    positionX: Number.isFinite(Number(logo.positionX)) ? Number(logo.positionX) : defaultLogoPosition,
    positionY: Number.isFinite(Number(logo.positionY)) ? Number(logo.positionY) : defaultLogoPosition
  };
};

const normalizeTabloShape = (
  tablo: TerminalTabloSettings | TerminalTabloSettingsFromBackend | null | undefined
): TerminalTabloSettings | null | undefined => {
  if (tablo == null) return tablo as null | undefined;
  if ('elements' in tablo && tablo.elements) {
    return tablo.logo != null ? { ...tablo, logo: normalizeTabloLogo(tablo.logo) } : (tablo as TerminalTabloSettings);
  }
  const flat = tablo as TerminalTabloSettingsFromBackend;
  return {
    ...flat,
    logo: normalizeTabloLogo(flat.logo),
    elements: {
      header: flat.header ?? ({} as TerminalTabloSettings['elements']['header']),
      video: flat.video ?? ({} as TerminalTabloSettings['elements']['video']),
      title: flat.title ?? ({} as TerminalTabloSettings['elements']['title']),
      queue: flat.queue ?? ({} as TerminalTabloSettings['elements']['queue']),
      takenTickets: flat.takenTickets ?? ({} as TerminalTabloSettings['elements']['takenTickets']),
      ticker: flat.ticker ?? ({} as TerminalTabloSettings['elements']['ticker']),
      slider: flat.slider ?? ({} as TerminalTabloSettings['elements']['slider']),
      weatherWidget:
        flat.weatherWidget ?? ({} as TerminalTabloSettings['elements']['weatherWidget'])
    }
  };
};

function parseIdFromUrl(paramName: string): number | null {
  if (typeof window === 'undefined') return null;
  const param = new URLSearchParams(window.location.search).get(paramName);
  if (param == null || param === '') return null;
  const id = parseInt(param, 10);
  return Number.isFinite(id) ? id : null;
}

export const useGetQueueTerminalAppearance = () => {
  const branchQueueId = parseIdFromUrl('branchQueueId');
  const branchId = parseIdFromUrl('branch');
  const hasMonitorToken = !!getMonitorToken();
  const {
    data: terminalAppearanceResponse,
    isFetching: isLoadingTerminalAppearance,
    isSuccess: isTerminalAppearanceReceived
  } = useQuery(
    ['QueueTerminalAppearance', branchQueueId, branchId, hasMonitorToken],
    () => {
      const params: Record<string, number> = {};
      if (branchQueueId != null) params.branchQueueId = branchQueueId;
      else if (branchId != null) params.branchId = branchId;
      if (hasMonitorToken) {
        const qs = queueScreenHeaders();
        return axios.get<TerminalAppearanceResponse>(TerminalAppearanceAPI.getAppearance, {
          ...qs,
          params: { ...(qs.params ?? {}), ...params }
        });
      }
      const base = commonHeaders();
      return axios.get<TerminalAppearanceResponse>(TerminalAppearanceAPI.getAppearance, {
        ...base,
        params: Object.keys(params).length > 0 ? params : undefined
      });
    },
    {
      select: ({ data }) => ({
        ...data,
        settings: data.settings
          ? {
              ...data.settings,
              tablo: normalizeTabloShape(
                data.settings?.tablo as TerminalTabloSettingsFromBackend | undefined
              )
            }
          : data.settings,
        backgrounds: (data.backgrounds || []).map((background) => ({
          ...background,
          url: normalizeMediaUrl(background.url)
        })),
        childrenBackgrounds: (data.childrenBackgrounds || []).map((background) => ({
          ...background,
          url: normalizeMediaUrl(background.url)
        }))
      }),
      refetchInterval: 30000
    }
  );

  return { terminalAppearanceResponse, isLoadingTerminalAppearance, isTerminalAppearanceReceived };
};
